import {produce} from 'immer';
import {isEqual, sortBy} from 'lodash';
import {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {Button} from '@/components/Button';
import {CloseIconComponent} from '@/components/Icons';
import {Typography} from '@/components/Typography';
import {KODATA_MAP_CATEGORIES} from '@/constants';
import {KodataCategory} from '@/enums';
import {KodataCategoryGroup} from '@/map/@types';
import {isValidKodataCategory} from '@/map/utils';
import {Flexbox, FlexCenter, interceptProps} from '@/styled-components';
import {palette} from '@/styles';
import {FullPageModal} from '../../../FullPageModal';

interface IProps {
  defaultOptions?: KodataCategoryGroup[];
  maxCount?: number;
  onComplete?(items: KodataCategoryGroup[]): void;
  onClose?(): void;
}

const MAX_COUNT = 3;
const cat1depth = Object.values(KodataCategory);
const cat1depthElementIdPrefix = 'sb-1depth-item-';

export const CategorySelectModal = ({
  defaultOptions,
  maxCount = MAX_COUNT,
  onComplete,
  onClose,
}: IProps) => {
  const [active1depth, setActive1Depth] = useState<KodataCategory>(
    cat1depth[0],
  );
  const [selected, setSelected] = useState<KodataCategoryGroup[]>([]);

  const cat2depth = useMemo<KodataCategoryGroup[]>(() => {
    if (!active1depth) return [];

    const cat: string[] = KODATA_MAP_CATEGORIES[active1depth] ?? [];

    return cat.sort().map((i) => ({
      category: active1depth,
      categoryDetail: i,
    }));
  }, [active1depth]);

  const onSelected = (cat: KodataCategoryGroup) => () => {
    const index = selected.findIndex((i) => isEqual(i, cat));

    if (index === -1 && selected.length + 1 > maxCount) {
      if (maxCount === 1) {
        return setSelected([cat]);
      }
      return;
    }

    setSelected(
      produce((draft) => {
        if (index === -1) {
          draft.push(cat);
        } else {
          draft.splice(index, 1);
        }
      }),
    );
  };

  const onClickGroup = (group: KodataCategory) => () => setActive1Depth(group);

  const handleComplete = () => {
    if (!selected.length) {
      onComplete?.([]);
    } else if (isEqual(sortBy(selected), sortBy(defaultOptions))) {
      onClose?.();
    } else {
      onComplete?.(selected);
    }
  };

  useEffect(() => {
    if (!selected?.length && defaultOptions?.length) {
      const next = defaultOptions
        .map((i) => {
          if (isValidKodataCategory(i)) {
            return i;
          }
          return undefined;
        })
        .filter((i) => !!i);

      setSelected(next as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOptions]);

  return (
    <FullPageModal>
      <Root>
        <Header>
          업종 선택
          {maxCount > 1 && (
            <span>({selected.length}/{maxCount})</span>
          )}
        </Header>
        <Options>
          <MainWrapper>
            <div>
              {cat1depth.map((i, idx) => (
                <MainItem
                  id={`${cat1depthElementIdPrefix}${idx}`}
                  key={i}
                  onClick={onClickGroup(i)}
                  active={i === active1depth}
                >
                  {i}
                  <Typography color={palette.gray500}>
                    {selected.filter((j) => j.category === i)?.length || ''}
                  </Typography>
                </MainItem>
              ))}
            </div>
          </MainWrapper>
          <SubWrapper>
            <div>
              {!!(active1depth && cat2depth?.length) &&
                cat2depth.map((i) => {
                  const active =
                    selected.findIndex((s) => isEqual(s, i)) !== -1;
                  return (
                    <SubItem
                      key={`${i.category}-${i.categoryDetail}`}
                      active={active}
                      onClick={onSelected(i)}
                    >
                      {i.categoryDetail}
                    </SubItem>
                  );
                })}
            </div>
          </SubWrapper>
        </Options>
        <Footer>
          <SelectedBox>
            {selected.map((i) => (
              <Flexbox key={i.categoryDetail}>
                {i.categoryDetail}
                <CloseIconComponent
                  color={palette.gray700}
                  onClick={onSelected(i)}
                />
              </Flexbox>
            ))}
          </SelectedBox>
          <ButtonGroup>
            <Button
              size="large"
              variant="outline"
              color="white"
              width={80}
              onClick={onClose}
            >
              닫기
            </Button>
            <Button
              size="large"
              variant="fill"
              color="primary"
              fullWidth
              onClick={handleComplete}
            >
              적용
            </Button>
          </ButtonGroup>
        </Footer>
      </Root>
    </FullPageModal>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled(FlexCenter)`
  color: ${palette.gray800};
  font-size: 16px;
  font-weight: 600;
  height: 60px;
  gap: 4px;
  border-bottom: 1px solid ${palette.gray150};
  flex-shrink: 0;
`;

const Footer = styled.div`
  border-top: 1px solid ${palette.gray150};
  flex-shrink: 0;
`;

const Options = styled.div`
  display: flex;
  flex-grow: 1;
  overflow: hidden;
`;

const Item = styled(Flexbox).withConfig({
  shouldForwardProp: interceptProps(['active']),
})<{
  active: boolean;
}>`
  cursor: pointer;
  & > * {
    flex-shrink: 0;
  }
`;

const MainWrapper = styled.div`
  background-color: ${palette.gray100};
  border-right: 1px solid ${palette.gray150};
  overflow: auto;
  flex-shrink: 0;
`;

const SubWrapper = styled.div`
  flex-grow: 1;
  overflow: auto;
  & > div {
    display: flex;
    padding: 16px 22px 16px 16px;
    align-items: flex-start;
    align-content: flex-start;
    gap: 12px 8px;
    flex-shrink: 0;
    align-self: stretch;
    flex-wrap: wrap;
  }
`;

const MainItem = styled(Item)`
  color: ${palette.gray500};
  padding: 20px 24px 20px 20px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.408px;
  display: flex;
  gap: 8px;
  ${({active}) =>
    active &&
    `
    font-weight: 700;
    background-color: ${palette.white};
    color: ${palette.black};
  `};
`;

const SubItem = styled(Item)`
  padding: 10px 18px;
  align-items: flex-start;
  border-radius: 999px;
  border: 1px solid ${palette.gray200};
  color: ${palette.gray700};
  font-size: 16px;
  font-weight: 500;
  ${({active}) =>
    active &&
    `
    font-weight: 700;
    color: ${palette.teal100};
    border: 1px solid ${palette.teal100};
background-color: v${palette.bgTeal100};
  `}
`;

const SelectedBox = styled.div`
  display: flex;
  gap: 8px;
  color: ${palette.teal100};
  font-size: 16px;
  font-weight: 500;
  overflow-x: auto;
  padding: 16px;
  white-space: nowrap;
`;

const ButtonGroup = styled.div`
  padding: 0 16px 10px;
  display: flex;
  gap: 8px;
`;
